import React, { useState, useEffect, useRef } from 'react';
import { FiFilter, FiX, FiCalendar } from 'react-icons/fi';

const TableFilters = ({ 
  onFiltersChange, 
  theme, 
  className = "",
  showDateRange = true,
  showAmountRange = true,
  showSearch = false,
  filters = {},
  defaultFilters = {},
  sortFieldOptions = [
    { value: 'date', label: 'Date' },
    { value: 'amount', label: 'Amount' },
    { value: 'created_at', label: 'Created At' },
    { value: 'paid_at', label: 'Paid At' }
  ]
}) => {
  // Helper function to format dates consistently without timezone issues
  const formatLocalDate = (date) => {
    return date.getFullYear() + '-' + 
      String(date.getMonth() + 1).padStart(2, '0') + '-' + 
      String(date.getDate()).padStart(2, '0');
  };
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    date_from: undefined,
    date_to: undefined,
    amount_min: undefined,
    amount_max: undefined,
    // search: undefined, // Temporarily disabled
    sort_by: 'date',
    sort_order: 'desc',
    ...defaultFilters
  });

  // Calendar picker states
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  const [fromCalendarMonth, setFromCalendarMonth] = useState(new Date().getMonth());
  const [fromCalendarYear, setFromCalendarYear] = useState(new Date().getFullYear());
  const [toCalendarMonth, setToCalendarMonth] = useState(new Date().getMonth());
  const [toCalendarYear, setToCalendarYear] = useState(new Date().getFullYear());

  // Refs for click outside detection
  const fromCalendarRef = useRef(null);
  const toCalendarRef = useRef(null);

  // Sync local state with parent filters
  useEffect(() => {
    if (filters && Object.keys(filters).length > 0) {
      setLocalFilters(prev => ({ ...prev, ...filters }));
    }
  }, [filters]);

  // Click outside handler for calendars
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromCalendarRef.current && !fromCalendarRef.current.contains(event.target)) {
        setShowFromCalendar(false);
      }
      if (toCalendarRef.current && !toCalendarRef.current.contains(event.target)) {
        setShowToCalendar(false);
      }
    };

    if (showFromCalendar || showToCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFromCalendar, showToCalendar]);

  const handleFilterChange = (key, value) => {
    // Convert empty strings to undefined for better API handling
    const cleanValue = value === '' ? undefined : value;
    const newFilters = { ...localFilters, [key]: cleanValue };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      date_from: undefined,
      date_to: undefined,
      amount_min: undefined,
      amount_max: undefined,
      // search: undefined, // Temporarily disabled
      sort_by: defaultFilters.sort_by || 'date',
      sort_order: defaultFilters.sort_order || 'desc'
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(localFilters).some(value => 
    value !== undefined && 
    value !== (defaultFilters.sort_by || 'date') && 
    value !== (defaultFilters.sort_order || 'desc')
  );

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const setQuickDateRange = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const newFilters = {
      ...localFilters,
      date_from: formatLocalDate(startDate),
      date_to: formatLocalDate(endDate)
    };
    
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearDateFilters = () => {
    const newFilters = {
      ...localFilters,
      date_from: undefined,
      date_to: undefined
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const renderCalendar = (isVisible, onClose, currentValue, onChange, maxDate = null, month, year, setMonth, setYear) => {
    if (!isVisible) return null;

    const today = new Date();

    const getDaysInMonth = (month, year) => {
      return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (month, year) => {
      return new Date(year, month, 1).getDay();
    };

    const handleDateSelect = (day) => {
      const selectedDate = new Date(year, month, day);
      const formattedDate = formatLocalDate(selectedDate);
      onChange(formattedDate);
      onClose();
    };

    const goToPreviousMonth = () => {
      if (month === 0) {
        setMonth(11);
        setYear(year - 1);
      } else {
        setMonth(month - 1);
      }
    };

    const goToNextMonth = () => {
      if (month === 11) {
        setMonth(0);
        setYear(year + 1);
      } else {
        setMonth(month + 1);
      }
    };

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const daysInMonth = getDaysInMonth(month, year);
    const firstDayOfMonth = getFirstDayOfMonth(month, year);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = currentValue === formatLocalDate(date);
      const isDisabled = maxDate && date > new Date(maxDate);
      
      days.push(
        <button
          key={day}
          onClick={() => !isDisabled && handleDateSelect(day)}
          disabled={isDisabled}
          className={`
            p-2 w-8 h-8 rounded-full text-sm transition-colors
            ${isSelected 
              ? 'bg-blue-600 text-white' 
              : isToday 
                ? 'bg-gray-200 text-gray-800' 
                : 'hover:bg-gray-100 text-gray-700'
            }
            ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="absolute top-full left-0 z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 min-w-[280px]">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPreviousMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-lg font-semibold text-gray-800">
            {monthNames[month]} {year}
          </h3>
          <button
            onClick={goToNextMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-2 text-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
        
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
          style={{ 
            backgroundColor: theme.colors.background.secondary,
            color: theme.colors.text.primary,
            border: `1px solid ${theme.colors.background.accent}`
          }}
        >
          <FiFilter className="h-4 w-4" />
          Filters
          {hasActiveFilters && (
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          )}
        </button>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-400 hover:text-red-300 transition-colors"
          >
            <FiX className="h-4 w-4" />
            Clear All
          </button>
        )}
      </div>

      {isExpanded && (
        <div 
          className="space-y-6 p-4 rounded-lg mb-4"
          style={{ backgroundColor: theme.colors.background.secondary }}
        >
          {/* Date Range Section */}
          {showDateRange && (
            <div>
              <label className="block text-sm font-medium mb-3" style={{ color: theme.colors.text.primary }}>
                Date Range
              </label>
              
              {/* Quick Date Presets */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => setQuickDateRange(7)}
                  className="px-3 py-1 text-xs rounded border transition-colors hover:bg-blue-600 hover:text-white"
                  style={{ 
                    borderColor: theme.colors.background.accent,
                    color: theme.colors.text.primary
                  }}
                >
                  Last 7 days
                </button>
                <button
                  onClick={() => setQuickDateRange(30)}
                  className="px-3 py-1 text-xs rounded border transition-colors hover:bg-blue-600 hover:text-white"
                  style={{ 
                    borderColor: theme.colors.background.accent,
                    color: theme.colors.text.primary
                  }}
                >
                  Last 30 days
                </button>
                <button
                  onClick={() => setQuickDateRange(90)}
                  className="px-3 py-1 text-xs rounded border transition-colors hover:bg-blue-600 hover:text-white"
                  style={{ 
                    borderColor: theme.colors.background.accent,
                    color: theme.colors.text.primary
                  }}
                >
                  Last 90 days
                </button>
                <button
                  onClick={clearDateFilters}
                  className="px-3 py-1 text-xs rounded border transition-colors hover:bg-red-600 hover:text-white"
                  style={{ 
                    borderColor: theme.colors.background.accent,
                    color: theme.colors.text.primary
                  }}
                >
                  Clear Dates
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative" ref={fromCalendarRef}>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                    Date From
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={localFilters.date_from ? formatDateForDisplay(localFilters.date_from) : ''}
                      onClick={() => setShowFromCalendar(true)}
                      readOnly
                      placeholder="Click to select date"
                      className="w-full p-2 pr-10 rounded border text-sm cursor-pointer"
                      style={{ 
                        backgroundColor: theme.colors.background.primary,
                        color: theme.colors.text.primary,
                        borderColor: theme.colors.background.accent
                      }}
                    />
                    <FiCalendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {renderCalendar(
                    showFromCalendar,
                    () => setShowFromCalendar(false),
                    localFilters.date_from,
                    (date) => handleFilterChange('date_from', date),
                    localFilters.date_to ? new Date(localFilters.date_to) : null,
                    fromCalendarMonth,
                    fromCalendarYear,
                    setFromCalendarMonth,
                    setFromCalendarYear
                  )}
                </div>
                
                <div className="relative" ref={toCalendarRef}>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                    Date To
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={localFilters.date_to ? formatDateForDisplay(localFilters.date_to) : ''}
                      onClick={() => setShowToCalendar(true)}
                      readOnly
                      placeholder="Click to select date"
                      className="w-full p-2 pr-10 rounded border text-sm cursor-pointer"
                      style={{ 
                        backgroundColor: theme.colors.background.primary,
                        color: theme.colors.text.primary,
                        borderColor: theme.colors.background.accent
                      }}
                    />
                    <FiCalendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {renderCalendar(
                    showToCalendar,
                    () => setShowToCalendar(false),
                    localFilters.date_to,
                    (date) => handleFilterChange('date_to', date),
                    null,
                    toCalendarMonth,
                    toCalendarYear,
                    setToCalendarMonth,
                    setToCalendarYear
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Amount Range Section */}
          {showAmountRange && (
            <div>
              <label className="block text-sm font-medium mb-3" style={{ color: theme.colors.text.primary }}>
                Amount Range
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                    Min Amount (₦)
                  </label>
                  <input
                    type="number"
                    value={localFilters.amount_min || ''}
                    onChange={(e) => handleFilterChange('amount_min', e.target.value)}
                    placeholder="0"
                    className="w-full p-2 rounded border text-sm"
                    style={{ 
                      backgroundColor: theme.colors.background.primary,
                      color: theme.colors.text.primary,
                      borderColor: theme.colors.background.accent
                    }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                    Max Amount (₦)
                  </label>
                  <input
                    type="number"
                    value={localFilters.amount_max || ''}
                    onChange={(e) => handleFilterChange('amount_max', e.target.value)}
                    placeholder="1000000"
                    className="w-full p-2 rounded border text-sm"
                    style={{ 
                      backgroundColor: theme.colors.background.primary,
                      color: theme.colors.text.primary,
                      borderColor: theme.colors.background.accent
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Sort Options Section */}
          <div>
            <label className="block text-sm font-medium mb-3" style={{ color: theme.colors.text.primary }}>
              Sort Options
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                  Sort By
                </label>
                <select
                  value={localFilters.sort_by}
                  onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                  className="w-full p-2 rounded border text-sm"
                  style={{ 
                    backgroundColor: theme.colors.background.primary,
                    color: theme.colors.text.primary,
                    borderColor: theme.colors.background.accent
                  }}
                >
                  {sortFieldOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                  Sort Order
                </label>
                <select
                  value={localFilters.sort_order}
                  onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                  className="w-full p-2 rounded border text-sm"
                  style={{ 
                    backgroundColor: theme.colors.background.primary,
                    color: theme.colors.text.primary,
                    borderColor: theme.colors.background.accent
                  }}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableFilters;
